import { useRef, useState } from "react";
import { Loader2, PenLine, Sparkles, SpellCheck, StepForward } from "lucide-react";
import { StressView } from "./StressView";
import { toast } from "sonner";
import { getPref, streamLyrics } from "../lib/api";
import { LLMS, LYRIC_TAGS } from "../lib/presets";
import { inputCls } from "./Chips";

const AiBtn = ({ testId, onClick, busy, icon: Icon, children }) => (
  <button type="button" data-testid={testId} onClick={onClick} disabled={busy}
    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[var(--c-violet)]/40 text-[var(--c-violet)] hover:bg-[var(--c-violet)] hover:text-white transition-colors duration-200 disabled:opacity-50">
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}{children}
  </button>
);

const useLyricsAI = ({ value, onChange, idea, style, length, llm }) => {
  const [busy, setBusy] = useState("");
  const run = async (action) => {
    if (action !== "write" && !value.trim()) return toast.error("Pirmiausia įrašykite tekstą");
    setBusy(action);
    try {
      await streamLyrics({ action, idea, style, existing: value, length, llm: llm || getPref("llm", LLMS) }, (t) => onChange(t.trimStart()));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };
  return { busy, run };
};

const TagBar = ({ onInsert }) => (
  <div className="flex flex-wrap gap-1.5 p-3 border-b border-[var(--c-line)]">
    {LYRIC_TAGS.map((t) => (
      <button key={t} type="button" data-testid={`lyrics-tag-${t.replace(/[[\]]/g, "")}`} onClick={() => onInsert(t)}
        className="font-mono text-[11px] px-2 py-1 rounded-md bg-[var(--c-orange)]/10 text-[var(--c-orange)] border border-[var(--c-orange)]/25 hover:bg-[var(--c-orange)] hover:text-white transition-colors">
        {t}
      </button>
    ))}
  </div>
);

const AI_ACTIONS = [["write", "AI parašyk", Sparkles], ["improve", "Patobulink", PenLine], ["continue", "Pratęsk", StepForward], ["accent", "AI sukirčiuok", SpellCheck]];

export const LyricsEditor = ({ value, onChange, idea, style, length, llm }) => {
  const ref = useRef(null);
  const [stress, setStress] = useState(false);
  const { busy, run } = useLyricsAI({ value, onChange, idea, style, length, llm });

  const insert = (tag) => {
    const el = ref.current;
    const pos = el?.selectionStart ?? value.length;
    const pre = value.slice(0, pos);
    const add = `${pre && !pre.endsWith("\n") ? "\n" : ""}${tag}\n`;
    onChange(pre + add + value.slice(pos));
    setTimeout(() => { el?.focus(); el?.setSelectionRange(pos + add.length, pos + add.length); }, 0);
  };

  const lines = value.split("\n").filter((l) => l.trim() && !l.trim().startsWith("["));
  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] overflow-hidden focus-within:border-[var(--c-orange)] transition-colors">
      <TagBar onInsert={insert} />
      {busy && (
        <div data-testid="lyrics-ai-busy" className="px-4 py-2 text-xs text-[var(--c-violet)] shimmer flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />AI rašo lietuviškus žodžius… tai gali užtrukti iki minutės
        </div>
      )}
      {stress ? (
        <>
          <p data-testid="stress-hint" className="px-4 pt-3 text-xs text-zinc-400">Spustelėkite balsę, kurią reikia kirčiuoti. Kirtis padeda vokalui teisingai ištarti žodį.</p>
          <StressView value={value} onChange={onChange} />
        </>
      ) : <textarea ref={ref} data-testid="lyrics-editor-textarea" value={value} onChange={(e) => onChange(e.target.value)} rows={12}
        placeholder={"[Strofa]\nRašyk savo žodžius čia...\n\n[Priedainis]\n..."}
        className={`${inputCls} border-0 rounded-none bg-transparent font-mono leading-7 resize-y scroll-thin focus:border-0`} />}
      <div className="flex flex-wrap items-center gap-2 p-3 border-t border-[var(--c-line)]">
        {AI_ACTIONS.map(([a, label, icon]) => (
          <AiBtn key={a} testId={`lyrics-ai-${a}`} onClick={() => run(a)} busy={busy === a} icon={icon}>{label}</AiBtn>
        ))}
        <button type="button" data-testid="lyrics-stress-toggle" onClick={() => setStress(!stress)}
          className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${stress ? "bg-[var(--c-orange)] border-[var(--c-orange)] text-white" : "border-[var(--c-line)] text-zinc-300 hover:border-zinc-500"}`}>
          {stress ? "Baigti kirčiuoti" : "Kirčiai"}
        </button>
        <span data-testid="lyrics-stats" className="ml-auto text-[11px] font-mono text-zinc-500">{lines.length} eil. · {value.length}/3500</span>
      </div>
    </div>
  );
};
